import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BlockFieldMap, setActiveBlockFields } from 'state/slices/activeBlockFieldSlice';
import { RootState } from 'state/store';

interface UseRegisterBlockForDisplayLogicOptions {
  /**
   * The new field mappings to register
   */
  fields: BlockFieldMap;
  /**
   * Dependencies for the effect
   * - Use [] (default) for one-time registration on mount (most common)
   * - Use [customType] or other deps if fields change based on props
   */
  deps?: React.DependencyList;
  /**
   * If true, skips registration
   * Useful for conditional registration based on array length, etc.
   */
  skip?: boolean;
}

/**
 * Registers block fields in the Redux store for display logic evaluation.
 * Automatically merges with existing blockFieldMap to prevent overwrites.
 *
 * @example
 * // Simple case - register once on mount
 * useRegisterBlockForDisplayLogic({
 *   fields: getFieldMappingForBlockType({ blockType, configuration, formFieldPrefix }),
 * });
 *
 * @example
 * // With custom dependencies
 * useRegisterBlockForDisplayLogic({
 *   fields: getFieldMappingForBlockType({ blockType: customType, configuration, formFieldPrefix }),
 *   deps: [customType],
 * });
 *
 * @example
 * // Conditional registration
 * useRegisterBlockForDisplayLogic({
 *   fields: fieldMap,
 *   skip: fields.length === 0,
 * });
 */
export function useRegisterDynamicallyAddedBlockForDisplayLogic(
  options: UseRegisterBlockForDisplayLogicOptions,
) {
  const { fields, deps = [], skip = false } = options;

  const dispatch = useDispatch();
  const { blockFieldMap } = useSelector((state: RootState) => state.blockFieldMap);

  useEffect(() => {
    if (skip) return;

    dispatch(setActiveBlockFields([...blockFieldMap, ...fields]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
